// "My work" — every workspace's own dashboard (2026-10-08; backend
// routers/my_work.py, open_issues.py, my_work_rules.py, work_policies.py;
// design: reports/role_dashboards/design.md).
//
// Layout, top to bottom:
//   - 3-6 headline stats for this workspace (my projects, pipeline months, ...)
//   - "Open issues": ONE ROW PER CATEGORY with a count. Clicking the count
//     expands the top 5 by priority; "Show all" lists the rest. Each issue
//     links to its subject (project, brief, asset issue, admin tab) and can be
//     hidden or snoozed — for the caller only — or, for rules that allow it,
//     resolved ("acknowledge"). Hidden / snoozed ones live behind a link.
//   - Policies: the thresholds behind the rules. The owner team's managers
//     (or an admin) edit them here; everyone else sees them read-only.
//   - Admins: the rule catalogue with each rule's last run / error.
//
// Issues are opened / auto-resolved by the backend rule engine (on a
// schedule, and cheaply when this page loads); the bell gets ONE
// notification when an issue opens (or a digest when many open at once).
//
// Lives in the light half of the app (main.ts) so the field workspaces can
// use it without loading the map; links to map things load the rest first.
/* eslint-disable @typescript-eslint/no-explicit-any */
import { directionsControls } from "./directions";
import { getJson, postJson } from "./api";
import { approvalsWaiting, canUseApprovals, openApprovals } from "./approvalsInbox";
import { el } from "./legend";
import { getState, roleAtLeast } from "./state";
import "./styles/myWork.css";
import { showToast } from "./toast";
import { insufficientEl } from "./sampleStats";
import { currentWorkspace, switchWorkspace, workspaceLabel } from "./workspaces";
import type { WorkspaceKey } from "./workspaces";

type Severity = "info" | "warning" | "critical";

interface Stat {
  key: string;
  label: string;
  value: string | number;
  hint?: string;
  severity?: Severity | null;
  link?: Link | null;
  /** sparse data (sample_stats.py): false = `value` is already the
   * replacement summary and `note` says what is missing */
  sufficient?: boolean;
  n?: number | null;
  min_n?: number | null;
  note?: string | null;
}

interface Link {
  type: string;
  id?: string | number;
  tab?: string;
  phase?: string;
  asset_id?: string;
}

interface Category {
  key: string;
  label: string;
  description: string;
  count: number;
  severity: Severity;
  by_severity: Record<Severity, number>;
  can_resolve: boolean;
}

interface Summary {
  workspace: string;
  categories: Category[];
  total: number;
  hidden: number;
  snoozed: number;
  top_n: number;
  stats: Stat[];
  stats_error: string | null;
  has_policies: boolean;
}

interface Issue {
  id: number;
  rule: string;
  title: string;
  detail: string | null;
  severity: Severity;
  link: Link | null;
  first_seen: string;
  last_seen: string;
  hidden: boolean;
  snoozed_until: string | null;
  can_resolve: boolean;
}

interface Policy {
  key: string;
  label: string;
  owner_team: string;
  unit: string;
  help: string;
  min: number;
  max: number;
  value: number;
  default: number;
  is_default: boolean;
  can_edit: boolean;
  changed_at: string | null;
  changed_by: string | null;
  reason: string | null;
}

const SEV_LABEL: Record<Severity, string> = { critical: "Critical", warning: "Needs attention", info: "For information" };
const OWNER_LABEL: Record<string, string> = {
  strategy: "strategy & planning managers", procurement: "procurement managers", delivery: "delivery managers",
  asset_data: "asset data managers", admin: "admins",
};

/** Expanded categories (rule key -> how many shown), kept across re-renders. */
const expanded = new Map<string, number | "all">();
let pendingFocus: { rule?: string; issueId?: number } | null = null;
let rendering = 0;

const flowNav = () => {
  document.dispatchEvent(new CustomEvent("wcc-full-app-needed"));
  return import("./flowNav");
};

function container(): HTMLElement | null {
  return document.getElementById("my-work-container");
}

function isActive(): boolean {
  return !!document.getElementById("tab-myWork")?.classList.contains("active");
}

function workspace(): WorkspaceKey {
  return currentWorkspace();
}

function ago(iso: string): string {
  const d = new Date(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 60) return mins < 1 ? "just now" : `${mins} min ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)} h ago`;
  const days = Math.round(mins / 1440);
  return days === 1 ? "yesterday" : `${days} days ago`;
}

function sevDot(sev: Severity): HTMLElement {
  const s = el("span", { className: `mw-dot mw-${sev}`, title: SEV_LABEL[sev] });
  s.setAttribute("aria-label", SEV_LABEL[sev]);
  return s;
}

function clickTab(tab: string): void {
  document.querySelector<HTMLButtonElement>(`#sidebar .tab-btn[data-tab="${tab}"]`)?.click();
}

/** Navigate to an issue's subject. */
function openLink(link: Link | null): void {
  if (!link) return;
  if (link.type === "project" && link.id) {
    const id = String(link.id);
    // `tab` / `phase`: the full-width project workspace's tab (e.g. a gateway flag -> Gateways)
    void flowNav().then((m) => m.openProjectById(id, { tab: link.tab ?? null, phase: link.phase ?? null }))
      .catch(() => showToast("Couldn't open that project"));
  } else if (link.type === "brief" && link.id) {
    const id = String(link.id);
    void flowNav().then((m) => m.openBrief(id));
  } else if (link.type === "asset_issue" && link.asset_id) {
    const asset = link.asset_id;
    document.dispatchEvent(new CustomEvent("wcc-full-app-needed"));
    void import("./assetIssues").then((m) => m.showAssetIssues(asset));
  } else if (link.tab) {
    clickTab(link.tab);
  }
}

function linkLabel(link: Link | null): string | null {
  if (!link) return null;
  if (link.type === "project") return "Open project";
  if (link.type === "brief") return "Open brief";
  if (link.type === "asset_issue") return "Open in the issue queue";
  if (link.tab) {
    const btn = document.querySelector<HTMLButtonElement>(`#sidebar .tab-btn[data-tab="${link.tab}"]`);
    return btn?.textContent?.trim() ? `Go to ${btn.textContent.trim()}` : "Open";
  }
  return null;
}

// ---------------------------------------------------------------------------
// Stats
// ---------------------------------------------------------------------------

function renderStats(stats: Stat[], error: string | null): HTMLElement {
  const grid = el("div", { className: "mw-stats" });
  for (const s of stats) {
    const sparse = s.sufficient === false;
    const tile = el("div", { className: `mw-stat${s.severity ? ` mw-stat-${s.severity}` : ""}${sparse ? " mw-stat-sparse" : ""}` });
    tile.append(el("div", { className: "mw-stat-label" }, s.label),
      el("div", { className: `mw-stat-value${sparse ? " sparse-value" : ""}` }, String(s.value)));
    if (s.hint) tile.append(el("div", { className: "caption" }, s.hint));
    if (sparse) {
      const note = el("div", { className: "caption" });
      note.append(insufficientEl({ n: s.n, min_n: s.min_n, note: s.note }));
      tile.append(note);
    }
    if (s.link) {
      tile.classList.add("mw-clickable");
      tile.tabIndex = 0;
      tile.title = linkLabel(s.link) ?? "";
      const go = () => openLink(s.link ?? null);
      tile.addEventListener("click", go);
      tile.addEventListener("keydown", (e) => {
        if (e.key === "Enter") go();
      });
    }
    grid.append(tile);
  }
  if (error) grid.append(el("p", { className: "caption mw-error" }, `Some stats couldn't be computed: ${error}`));
  return grid;
}

// ---------------------------------------------------------------------------
// Issues
// ---------------------------------------------------------------------------

async function act(issue: Issue, action: "hide" | "snooze" | "restore" | "resolve", days?: number): Promise<void> {
  try {
    const body = action === "snooze" ? { days } : {};
    await postJson(`/api/my-work/issues/${issue.id}/${action}`, body);
    const msg = { hide: "Hidden for you", snooze: `Snoozed for ${days} day${days === 1 ? "" : "s"}`, restore: "Restored", resolve: "Resolved" }[action];
    showToast(msg);
  } catch (err) {
    showToast(err instanceof Error ? err.message : "That didn't work");
  }
  void render();
}

function issueRow(issue: Issue, hiddenList = false): HTMLElement {
  const li = el("li", { className: `mw-issue mw-issue-${issue.severity}` });
  li.dataset.issueId = String(issue.id);
  const head = el("div", { className: "mw-issue-head" });
  head.append(sevDot(issue.severity));
  const label = linkLabel(issue.link);
  if (issue.link && label) {
    const a = el("button", { className: "link-button mw-issue-title", title: label, type: "button" }, issue.title);
    a.addEventListener("click", () => openLink(issue.link));
    head.append(a);
  } else {
    head.append(el("span", { className: "mw-issue-title" }, issue.title));
  }
  li.append(head);
  if (issue.detail) li.append(el("div", { className: "caption mw-issue-detail" }, issue.detail));
  const meta = el("div", { className: "mw-issue-actions" });
  meta.append(el("span", { className: "caption" }, `open since ${ago(issue.first_seen)}`));
  if (hiddenList) {
    if (issue.snoozed_until) meta.append(el("span", { className: "caption" }, ` · snoozed until ${new Date(issue.snoozed_until).toLocaleDateString()}`));
    if (issue.hidden) meta.append(el("span", { className: "caption" }, " · hidden"));
    const restore = el("button", { className: "link-button", type: "button" }, "Restore");
    restore.addEventListener("click", () => void act(issue, "restore"));
    meta.append(restore);
  } else {
    const snooze = el("select", { className: "mw-snooze", title: "Snooze for you only — it comes back afterwards if still open" }) as HTMLSelectElement;
    snooze.append(el("option", { value: "" }, "Snooze…"));
    for (const [d, t] of [[1, "1 day"], [7, "1 week"], [30, "30 days"]] as const) snooze.append(el("option", { value: String(d) }, t));
    snooze.addEventListener("change", () => {
      if (snooze.value) void act(issue, "snooze", Number(snooze.value));
    });
    // directions to the project / asset this item is about (2026-10-08)
    const l = issue.link;
    if (l?.type === "project" && l.id) meta.append(directionsControls({ projectId: String(l.id) }, { compact: true }));
    else if (l?.asset_id) meta.append(directionsControls({ assetId: l.asset_id }, { compact: true }));
    const hide = el("button", { className: "link-button", type: "button", title: "Hide for you only — it stays open for everyone else" }, "Hide");
    hide.addEventListener("click", () => void act(issue, "hide"));
    meta.append(snooze, hide);
    if (issue.can_resolve) {
      const res = el("button", { className: "link-button", type: "button", title: "Mark as dealt with — it won't come back for this item" }, "Resolve");
      res.addEventListener("click", () => void act(issue, "resolve"));
      meta.append(res);
    }
  }
  li.append(meta);
  return li;
}

async function loadIssues(cat: Category, list: HTMLElement, foot: HTMLElement, topN: number): Promise<void> {
  const shown = expanded.get(cat.key) ?? topN;
  const limit = shown === "all" ? 500 : shown;
  list.replaceChildren(el("li", { className: "caption" }, "Loading…"));
  try {
    const q = new URLSearchParams({ workspace: workspace(), rule: cat.key, limit: String(limit) });
    const res = await getJson<{ items: Issue[]; total: number }>(`/api/my-work/issues?${q}`);
    list.replaceChildren(...res.items.map((i) => issueRow(i)));
    foot.replaceChildren();
    if (res.total > res.items.length) {
      const more = el("button", { className: "link-button", type: "button" }, `Show all ${res.total}`);
      more.addEventListener("click", () => {
        expanded.set(cat.key, "all");
        void loadIssues(cat, list, foot, topN);
      });
      foot.append(more);
    } else if (shown === "all" && res.total > topN) {
      const fewer = el("button", { className: "link-button", type: "button" }, `Show top ${topN}`);
      fewer.addEventListener("click", () => {
        expanded.set(cat.key, topN);
        void loadIssues(cat, list, foot, topN);
      });
      foot.append(fewer);
    }
    if (pendingFocus?.issueId) {
      const row = list.querySelector<HTMLElement>(`[data-issue-id="${pendingFocus.issueId}"]`);
      if (row) {
        row.classList.add("mw-focus");
        row.scrollIntoView({ block: "nearest" });
      }
      pendingFocus = null;
    }
  } catch {
    list.replaceChildren(el("li", { className: "caption mw-error" }, "Couldn't load these issues."));
  }
}

function categoryRow(cat: Category, topN: number): HTMLElement {
  const row = el("div", { className: `mw-cat mw-cat-${cat.severity}` });
  const head = el("div", { className: "mw-cat-head" });
  const count = el("button", { className: `mw-count mw-${cat.severity}`, type: "button" }, String(cat.count));
  count.title = "Show the top issues";
  count.setAttribute("aria-expanded", "false");
  const label = el("div", { className: "mw-cat-label" });
  label.append(el("strong", {}, cat.label));
  const parts: string[] = [];
  if (cat.by_severity.critical) parts.push(`${cat.by_severity.critical} critical`);
  if (cat.by_severity.warning) parts.push(`${cat.by_severity.warning} need attention`);
  if (cat.by_severity.info && parts.length) parts.push(`${cat.by_severity.info} FYI`);
  if (parts.length) label.append(el("span", { className: "caption" }, ` · ${parts.join(", ")}`));
  label.title = cat.description;
  head.append(sevDot(cat.severity), label, count);
  row.append(head);
  const body = el("div", { className: "mw-cat-body" });
  const list = el("ul", { className: "mw-issues" });
  const foot = el("div", { className: "mw-cat-foot" });
  body.append(el("p", { className: "caption mw-cat-desc" }, cat.description), list, foot);
  body.hidden = true;
  row.append(body);
  const toggle = () => {
    body.hidden = !body.hidden;
    count.setAttribute("aria-expanded", String(!body.hidden));
    row.classList.toggle("mw-open", !body.hidden);
    if (body.hidden) expanded.delete(cat.key);
    else {
      if (!expanded.has(cat.key)) expanded.set(cat.key, topN);
      void loadIssues(cat, list, foot, topN);
    }
  };
  count.addEventListener("click", toggle);
  label.addEventListener("click", toggle);
  if (expanded.has(cat.key) || pendingFocus?.rule === cat.key) {
    if (pendingFocus?.rule === cat.key && pendingFocus.issueId) expanded.set(cat.key, "all");
    toggle();
  }
  return row;
}

async function renderHidden(host: HTMLElement): Promise<void> {
  host.replaceChildren(el("p", { className: "caption" }, "Loading…"));
  try {
    const res = await getJson<{ items: Issue[] }>(`/api/my-work/hidden?workspace=${encodeURIComponent(workspace())}`);
    const list = el("ul", { className: "mw-issues" });
    list.append(...res.items.map((i) => issueRow(i, true)));
    host.replaceChildren(res.items.length ? list : el("p", { className: "caption" }, "Nothing hidden."));
  } catch {
    host.replaceChildren(el("p", { className: "caption mw-error" }, "Couldn't load hidden issues."));
  }
}

// ---------------------------------------------------------------------------
// Policies
// ---------------------------------------------------------------------------

function fmtValue(p: Policy, v: number): string {
  return p.unit.startsWith("NZD") ? `$${v.toLocaleString()}` : `${v.toLocaleString()} ${p.unit}`;
}

function policyRow(p: Policy, rerender: () => void): HTMLElement {
  const row = el("div", { className: "mw-policy" });
  const head = el("div", { className: "mw-policy-head" });
  head.append(el("strong", {}, p.label));
  head.append(el("span", { className: "caption" }, ` — ${fmtValue(p, p.value)}${p.is_default ? " (default)" : ""}`));
  row.append(head, el("div", { className: "caption" }, p.help));
  if (!p.is_default && p.changed_at) {
    row.append(el("div", { className: "caption" },
      `Set by ${p.changed_by ?? "someone"} ${ago(p.changed_at)}${p.reason ? ` — "${p.reason}"` : ""}; default ${fmtValue(p, p.default)}.`));
  }
  if (!p.can_edit) {
    row.append(el("div", { className: "caption" }, `Set by the ${OWNER_LABEL[p.owner_team] ?? p.owner_team}.`));
    return row;
  }
  const form = el("form", { className: "mw-policy-form" });
  const input = el("input", { type: "number", value: String(p.value), min: String(p.min), max: String(p.max), step: "any" }) as HTMLInputElement;
  input.setAttribute("aria-label", p.label);
  const reason = el("input", { type: "text", placeholder: "Why (optional)", maxLength: 500 }) as HTMLInputElement;
  const save = el("button", { type: "submit" }, "Save");
  form.append(input, el("span", { className: "caption" }, p.unit), reason, save);
  if (!p.is_default) {
    const reset = el("button", { type: "button", className: "link-button" }, "Back to default");
    reset.addEventListener("click", () => void putPolicy(p.key, null, reason.value, rerender));
    form.append(reset);
  }
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const v = Number(input.value);
    if (!Number.isFinite(v)) return;
    void putPolicy(p.key, v, reason.value, rerender);
  });
  row.append(form);
  return row;
}

async function putPolicy(key: string, value: number | null, reason: string, rerender: () => void): Promise<void> {
  try {
    await getJson(`/api/my-work/policies/${encodeURIComponent(key)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ value, reason: reason || null }),
    });
    showToast("Policy saved — the rules use it from their next run");
    // re-run the cheap rules so the page reflects the new threshold
    await postJson("/api/my-work/refresh", {}).catch(() => {});
  } catch (err) {
    showToast(err instanceof Error ? err.message : "Couldn't save the policy");
  }
  rerender();
}

async function renderPolicies(host: HTMLElement): Promise<void> {
  host.replaceChildren(el("p", { className: "caption" }, "Loading…"));
  try {
    const res = await getJson<{ policies: Policy[] }>(`/api/my-work/policies?workspace=${encodeURIComponent(workspace())}`);
    host.replaceChildren(...res.policies.map((p) => policyRow(p, () => void render())));
    if (!res.policies.length) host.replaceChildren(el("p", { className: "caption" }, "No thresholds for this workspace."));
  } catch {
    host.replaceChildren(el("p", { className: "caption mw-error" }, "Couldn't load policies."));
  }
}

async function renderRules(host: HTMLElement): Promise<void> {
  host.replaceChildren(el("p", { className: "caption" }, "Loading…"));
  try {
    const res = await getJson<{ rules: any[] }>("/api/my-work/rules");
    const table = el("table", { className: "mw-rules" });
    const thead = el("tr");
    for (const h of ["Rule", "Dashboards", "Notifies", "Last run", "Status"]) thead.append(el("th", {}, h));
    table.append(thead);
    for (const r of res.rules) {
      const tr = el("tr");
      const name = el("td", { title: r.description }, r.label);
      tr.append(name, el("td", {}, (r.workspaces as string[]).join(", ")), el("td", {}, r.enabled ? r.notify : "disabled"),
        el("td", {}, r.last_run_at ? ago(r.last_run_at) : "never"),
        el("td", { className: r.last_error ? "mw-error" : "" }, r.last_error ?? (r.last_run_at ? `ok (${r.n_conditions ?? 0})` : "-")));
      table.append(tr);
    }
    host.replaceChildren(el("p", { className: "caption" }, "Rules run every 15 minutes (maintenance task my_work_rules) and, the cheap ones, when this page opens."), table);
  } catch {
    host.replaceChildren(el("p", { className: "caption mw-error" }, "Couldn't load the rule list."));
  }
}

function lazyDetails(title: string, load: (host: HTMLElement) => Promise<void>): HTMLElement {
  const det = el("details", { className: "mw-details" });
  det.append(el("summary", {}, title));
  const host = el("div");
  det.append(host);
  det.addEventListener("toggle", () => {
    if (det.open) void load(host);
  });
  return det;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export async function render(): Promise<void> {
  const root = container();
  if (!root) return;
  const ws = workspace();
  const ticket = ++rendering;
  if (!root.childElementCount) root.append(el("p", { className: "caption" }, "Loading your work…"));
  let data: Summary;
  try {
    data = await getJson<Summary>(`/api/my-work?workspace=${encodeURIComponent(ws)}`);
  } catch (err) {
    if (ticket !== rendering) return;
    root.replaceChildren(el("p", { className: "caption mw-error" }, err instanceof Error ? err.message : "Couldn't load My work."));
    return;
  }
  if (ticket !== rendering) return;
  const page = el("div", { className: "mw-page" });
  const head = el("div", { className: "mw-head" });
  head.append(el("h2", {}, `My work — ${workspaceLabel(ws)}`));
  const refresh = el("button", { type: "button", className: "link-button", title: "Re-check now" }, "Refresh");
  refresh.addEventListener("click", () => {
    refresh.disabled = true;
    postJson("/api/my-work/refresh", {})
      .catch(() => {})
      .finally(() => void render());
  });
  head.append(refresh);
  page.append(head);
  // Managers: decisions waiting on them live in one Approvals inbox (its
  // proposal / sign-off rows also show below as categories); link to it.
  if (canUseApprovals()) {
    const slot = el("div");
    page.append(slot);
    void approvalsWaiting().then((n) => {
      if (!n || ticket !== rendering) return;
      const banner = el("div", { className: "ap-banner" });
      banner.append(el("span", {}, `${n} decision${n === 1 ? "" : "s"} waiting for you`));
      const go = el("button", { type: "button" }, "Open Approvals");
      go.addEventListener("click", () => openApprovals());
      banner.append(go);
      slot.replaceChildren(banner);
    });
  }
  if (data.stats.length || data.stats_error) page.append(renderStats(data.stats, data.stats_error));

  const issues = el("section", { className: "mw-section" });
  issues.append(el("h3", {}, data.total ? `Open issues (${data.total})` : "Open issues"));
  if (!data.categories.length) {
    issues.append(el("p", { className: "caption mw-empty" }, "Nothing needs you right now."));
  }
  for (const cat of data.categories) issues.append(categoryRow(cat, data.top_n));
  if (data.hidden || data.snoozed) {
    const bits = [data.hidden ? `${data.hidden} hidden` : "", data.snoozed ? `${data.snoozed} snoozed` : ""].filter(Boolean).join(", ");
    issues.append(lazyDetails(`Hidden by you (${bits})`, renderHidden));
  }
  page.append(issues);

  if (data.has_policies) page.append(lazyDetails("Policies (thresholds behind these issues)", renderPolicies));
  if (roleAtLeast(getState().currentUser, "admin") && ws === "admin") page.append(lazyDetails("Rules and their last run", renderRules));
  if (ws === "strategy" && document.getElementById("dashboard-tab-btn")) {
    const old = el("p", { className: "caption" });
    const btn = el("button", { className: "link-button", type: "button" }, "the network dashboard");
    btn.addEventListener("click", () => clickTab("dashboard"));
    old.append(document.createTextNode("Network-wide charts are still on "), btn, document.createTextNode("."));
    page.append(old);
  }
  root.replaceChildren(page);
}

/** Open My work (optionally in another workspace, on one category / issue).
 * Used by the notification bell. */
export async function openMyWork(target: { workspace?: string; rule?: string; issueId?: number } = {}): Promise<void> {
  let ws = target.workspace;
  let rule = target.rule;
  if (target.issueId && (!ws || !rule)) {
    try {
      const iss = await getJson<{ rule: string; workspaces: string[] }>(`/api/my-work/issues/${target.issueId}`);
      rule = iss.rule;
      ws = iss.workspaces.includes(currentWorkspace()) ? currentWorkspace() : iss.workspaces[0];
    } catch {
      showToast("That issue has been resolved");
    }
  }
  if (ws && ws !== currentWorkspace() && !switchWorkspace(ws as WorkspaceKey)) {
    showToast("That's on a workspace you can't open");
    return;
  }
  pendingFocus = { rule, issueId: target.issueId };
  if (isActive()) void render();
  else clickTab("myWork");
}

/** Call once at boot, before initWorkspaces (whose landing click renders it). */
export function initMyWork(): void {
  const btn = document.getElementById("my-work-tab-btn");
  if (!btn) return;
  btn.addEventListener("click", () => void render());
  document.addEventListener("workspace-changed", () => {
    expanded.clear();
    const root = container();
    if (root) root.replaceChildren();
    if (isActive()) void render();
  });
  // keep an open page fresh (issues open / resolve in the background)
  window.setInterval(() => {
    if (isActive() && document.visibilityState === "visible") void render();
  }, 120_000);
}
